import React, { useState } from 'react';
import { TrendingUp, Calculator, ArrowRight, Sparkles } from 'lucide-react';

const GradeCalculator = () => {
  const [currentGrade, setCurrentGrade] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const grades = [
    { value: 1, label: '1', color: '#94A3B8' },
    { value: 2, label: '2', color: '#94A3B8' },
    { value: 3, label: '3', color: '#94A3B8' },
    { value: 4, label: '4', color: '#A88B4A' },
    { value: 5, label: '5', color: '#A88B4A' },
    { value: 6, label: '6', color: '#C5A059' },
    { value: 7, label: '7', color: '#C5A059' },
    { value: 8, label: '8', color: '#E5C575' },
    { value: 9, label: '9', color: '#0F172A' }
  ];

  const calculatePotentialGrade = (current) => {
    // Typically students improve 1-3 grades with consistent tuition
    const improvement = current <= 4 ? 2 : current <= 6 ? 2 : 1;
    return Math.min(9, current + improvement);
  };

  const getGradeColor = (grade) => {
    return grades.find(g => g.value === grade)?.color || '#C5A059';
  };

  const handleGradeSelect = (grade) => {
    setCurrentGrade(grade);
    setShowResult(true);
  };

  const potentialGrade = currentGrade ? calculatePotentialGrade(currentGrade) : null;

  return (
    <section style={{
      padding: '5rem 1.5rem',
      background: 'white'
    }}>
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(139, 92, 246, 0.1)',
            padding: '0.5rem 1rem',
            borderRadius: '9999px',
            marginBottom: '1rem'
          }}>
            <Calculator size={18} color="#0F172A" />
            <span style={{ color: '#0F172A', fontWeight: 600, fontSize: '0.9rem' }}>Grade Calculator</span>
          </div>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            See Your Potential Grade
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            Select your current grade to see what you could achieve with 3 months of consistent tuition
          </p>
        </div>

        {/* Grade selector */}
        <div style={{
          background: '#F8FAFC',
          borderRadius: '20px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <p style={{ 
            textAlign: 'center', 
            color: '#64748B', 
            marginBottom: '1.5rem',
            fontWeight: 500
          }}>
            What's your current GCSE Maths grade (or predicted grade)?
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            {grades.map((grade) => (
              <button
                key={grade.value}
                onClick={() => handleGradeSelect(grade.value)}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  border: currentGrade === grade.value ? `3px solid ${grade.color}` : '2px solid #E2E8F0',
                  background: currentGrade === grade.value ? grade.color : 'white',
                  color: currentGrade === grade.value ? 'white' : '#374151',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  transform: currentGrade === grade.value ? 'scale(1.1)' : 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  if (currentGrade !== grade.value) {
                    e.currentTarget.style.borderColor = grade.color;
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentGrade !== grade.value) {
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
                data-testid={`grade-${grade.value}`}
              >
                {grade.label}
              </button>
            ))}
          </div>
        </div>

        {/* Result */}
        {showResult && currentGrade && (
          <div style={{
            background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
            borderRadius: '20px',
            padding: '2.5rem',
            textAlign: 'center',
            animation: 'fadeIn 0.5s ease'
          }}>
            <Sparkles size={32} color="#E5C575" style={{ marginBottom: '1rem' }} />
            
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', marginBottom: '1.5rem' }}>
              Based on our students' average improvement...
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.5rem',
              marginBottom: '1.5rem',
              flexWrap: 'wrap'
            }}>
              {/* Current grade */}
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Current Grade
                </p>
                <div style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: 800,
                  color: 'white'
                }}>
                  {currentGrade}
                </div>
              </div>

              {/* Arrow */}
              <TrendingUp size={40} color="#E5C575" />

              {/* Potential grade */}
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: '#E5C575', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Potential Grade
                </p>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: '#E5C575',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: '#0F172A',
                  boxShadow: '0 0 30px rgba(255, 213, 79, 0.5)'
                }}>
                  {potentialGrade}
                </div>
              </div>
            </div>

            <p style={{ 
              color: 'white', 
              fontSize: '1.1rem', 
              marginBottom: '1.5rem',
              fontWeight: 600
            }}>
              You could improve by <span style={{ color: '#E5C575', fontSize: '1.3rem' }}>{potentialGrade - currentGrade} grade{potentialGrade - currentGrade > 1 ? 's' : ''}</span> in 3 months!
            </p>

            <p style={{ 
              color: 'rgba(255,255,255,0.8)', 
              fontSize: '0.9rem', 
              marginBottom: '2rem',
              maxWidth: '500px',
              margin: '0 auto 2rem'
            }}>
              * Based on students attending weekly sessions and completing homework. Individual results may vary.
            </p>

            <a
              href="https://calendly.com/drmomakobia/studentcall"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#E5C575',
                color: '#0F172A',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: '1rem'
              }}
              data-testid="calculator-cta"
            >
              Start Your Journey
              <ArrowRight size={20} />
            </a>
          </div>
        )}

        {/* CSS Animation */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </section>
  );
};

export default GradeCalculator;
